/**
 * Where a signed-in user should land once their profile/role is known:
 * admin > Team/Squad manager > plain profile. Used by both
 * storeFirebaseApp's post-login redirect (same-page sign-in submit) and the
 * router guard's redirect away from /login (direct navigation while already
 * authenticated) -- previously duplicated in both places, which let the two
 * paths drift out of sync.
 */
/** Admin landing page. Also gated by requiresAdmin, so the two must agree. */
export const ADMIN_LANDING_ROUTE = '/architecture'
export function resolvePostLoginRoute(firebaseAppStore: {
  currentUser: { role?: string } | null
  isManager: boolean
}): string {
  if (firebaseAppStore.currentUser?.role?.toLowerCase() === 'admin') {
    // Admin is configuration-only -- no default session/reports view, so it
    // lands on the architecture reference rather than an arbitrary
    // maintenance screen.
    return ADMIN_LANDING_ROUTE
  }
  if (firebaseAppStore.isManager) {
    // A Team/Squad manager (Director or Coach) lands on their hierarchy
    // view instead of the generic "my own sessions" page.
    return '/my-team'
  }
  return '/my-profile'
}

/** Where a signed-out user is sent -- the same place the router guard sends an
 * unauthenticated visitor who asks for a protected route. */
export const SIGNED_OUT_ROUTE = '/login'

/**
 * Whether signing out has to move the browser off the page it is on.
 *
 * Read off `meta.requiresAuth` rather than a list of paths: the rule is "a
 * signed-out user is never left sitting on a page that required a login", and
 * only the route table knows which those are. The previous path list ('/',
 * '/login', '/register' were left alone, everything else went home) got this
 * backwards in both directions -- it bounced a user off public pages like
 * /about and /oauth/consent, while any newly added protected route had to
 * remember to opt in.
 *
 * It matters most for the admin-only pages. /architecture and the /admin/*
 * views describe the whole platform -- where data lives, what the trust
 * boundaries are -- so a stale one left rendered after sign-out is an
 * admin-only page on screen with nobody signed in.
 */
export function requiresRedirectOnSignOut(route: {
  matched: { meta: { requiresAuth?: unknown } }[]
}): boolean {
  return route.matched.some(record => record.meta.requiresAuth === true)
}
